import type { CmmdProject, CmmdTask } from "@cmmd-forge/contracts";
import type { SidebarThreadSummary } from "~/types";

type CmmdAnchorKind = Extract<SidebarThreadSummary["anchorKind"], "task" | "project" | "campaign">;
type ThreadAnchorKind = SidebarThreadSummary["anchorKind"];

const WORKSPACE_NAME_SYNTHETIC_PREFIX = "__workspace_name__:";

export interface WorkspaceRef {
  readonly workspaceId: string | null;
  readonly workspaceName: string | null;
}

export interface WorkspaceScopedReferenceThread {
  readonly anchorKind: ThreadAnchorKind;
  readonly anchorCmmdId: string | null;
}

export interface ThreadCreationAnchor {
  readonly anchorKind: Exclude<ThreadAnchorKind, "scratch">;
  readonly anchorCmmdId: string;
}

function normalizeWhitespace(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

function normalizeStringValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeLookupToken(value: unknown): string {
  return normalizeStringValue(value)?.toLowerCase() ?? "";
}

function normalizeWorkspaceName(value: unknown): string | null {
  const stringValue = normalizeStringValue(value);
  if (!stringValue) return null;
  const normalized = normalizeWhitespace(stringValue);
  return normalized.length > 0 ? normalized : null;
}

export function toWorkspaceNameSyntheticId(workspaceName: string): string {
  const normalizedName = normalizeWorkspaceName(workspaceName);
  if (!normalizedName) {
    return `${WORKSPACE_NAME_SYNTHETIC_PREFIX}unknown`;
  }
  return `${WORKSPACE_NAME_SYNTHETIC_PREFIX}${normalizeLookupToken(normalizedName)}`;
}

export function isWorkspaceNameSyntheticId(workspaceId: string): boolean {
  return workspaceId.startsWith(WORKSPACE_NAME_SYNTHETIC_PREFIX);
}

function normalizeWorkspaceId(workspaceId: unknown): string | null {
  return normalizeStringValue(workspaceId);
}

function toWorkspaceRef(input: {
  readonly workspaceId?: unknown;
  readonly workspaceName?: unknown;
}): WorkspaceRef {
  return {
    workspaceId: normalizeWorkspaceId(input.workspaceId),
    workspaceName: normalizeWorkspaceName(input.workspaceName),
  };
}

function mergeWorkspaceRefs(
  existing: WorkspaceRef | undefined,
  incoming: WorkspaceRef,
): WorkspaceRef {
  if (!existing) return incoming;
  return {
    workspaceId: existing.workspaceId ?? incoming.workspaceId,
    workspaceName: existing.workspaceName ?? incoming.workspaceName,
  };
}

function stripCmmdPrefix(value: string): string {
  return value.startsWith("cmmd:") ? value.slice("cmmd:".length) : value;
}

function stripAnchorPrefix(value: string, kind: CmmdAnchorKind): string {
  const hyphenPrefix = `${kind}-`;
  if (value.startsWith(hyphenPrefix)) {
    return value.slice(hyphenPrefix.length);
  }
  const colonPrefix = `${kind}:`;
  if (value.startsWith(colonPrefix)) {
    return value.slice(colonPrefix.length);
  }
  return value;
}

function buildAnchorLookupKeys(anchorId: unknown, kind: CmmdAnchorKind): readonly string[] {
  const normalized = normalizeLookupToken(anchorId);
  if (normalized.length === 0) return [];
  const withoutCmmdPrefix = stripCmmdPrefix(normalized);
  const base = stripAnchorPrefix(withoutCmmdPrefix, kind);
  const prefixedNumericMatch = withoutCmmdPrefix.match(/^[a-z]+[_:-](\d+)$/i);
  const numericId = prefixedNumericMatch?.[1] ?? null;
  const keys = new Set<string>();
  keys.add(normalized);
  keys.add(withoutCmmdPrefix);
  keys.add(base);
  keys.add(`${kind}-${base}`);
  keys.add(`${kind}:${base}`);
  if (numericId) {
    keys.add(numericId);
    keys.add(`${kind}-${numericId}`);
    keys.add(`${kind}:${numericId}`);
  }
  return Array.from(keys.values());
}

function putRefByAnchorId(
  index: Map<string, WorkspaceRef>,
  anchorId: unknown,
  kind: CmmdAnchorKind,
  ref: WorkspaceRef,
): void {
  for (const key of buildAnchorLookupKeys(anchorId, kind)) {
    index.set(key, mergeWorkspaceRefs(index.get(key), ref));
  }
}

function normalizeTaskTitle(title: unknown): string {
  return normalizeLookupToken(title);
}

function projectMatchesWorkspace(
  project: Pick<CmmdProject, "workspaceId" | "workspaceName">,
  workspaceId: string,
  workspaceName: string | null,
): boolean {
  const normalizedProjectWorkspaceId = normalizeWorkspaceId(project.workspaceId);
  if (normalizedProjectWorkspaceId && normalizedProjectWorkspaceId === workspaceId) {
    return true;
  }
  const normalizedWorkspaceName = normalizeWorkspaceName(workspaceName);
  const normalizedProjectWorkspaceName = normalizeWorkspaceName(project.workspaceName);
  if (!normalizedWorkspaceName || !normalizedProjectWorkspaceName) {
    return false;
  }
  return (
    normalizeLookupToken(normalizedWorkspaceName) ===
    normalizeLookupToken(normalizedProjectWorkspaceName)
  );
}

export function resolveWorkspaceKeyFromRef(ref: WorkspaceRef | null | undefined): string | null {
  if (!ref) return null;
  if (ref.workspaceId) return ref.workspaceId;
  if (ref.workspaceName) return toWorkspaceNameSyntheticId(ref.workspaceName);
  return null;
}

export function buildProjectWorkspaceIndex(
  projects: ReadonlyArray<CmmdProject>,
): Map<string, WorkspaceRef> {
  const index = new Map<string, WorkspaceRef>();
  for (const project of projects) {
    const ref = toWorkspaceRef(project);
    putRefByAnchorId(index, project.id, "project", ref);
  }
  return index;
}

export function buildTaskWorkspaceIndex(
  tasks: ReadonlyArray<CmmdTask>,
  projectWorkspaceIndex: ReadonlyMap<string, WorkspaceRef>,
): Map<string, WorkspaceRef> {
  const index = new Map<string, WorkspaceRef>();
  for (const task of tasks) {
    const projectRef = task.projectId
      ? resolveRefFromIndex(projectWorkspaceIndex, task.projectId, "project")
      : null;
    const ref = toWorkspaceRef({
      workspaceId: task.workspaceId ?? projectRef?.workspaceId ?? null,
      workspaceName: task.workspaceName ?? projectRef?.workspaceName ?? null,
    });
    putRefByAnchorId(index, task.id, "task", ref);
  }
  return index;
}

export function buildCampaignWorkspaceIndex(
  projects: ReadonlyArray<CmmdProject>,
): Map<string, WorkspaceRef> {
  const index = new Map<string, WorkspaceRef>();
  for (const project of projects) {
    if (!project.campaignId) continue;
    const ref = toWorkspaceRef(project);
    putRefByAnchorId(index, project.campaignId, "campaign", ref);
  }
  return index;
}

export function buildWorkspaceKeyByTaskTitle(
  tasks: ReadonlyArray<CmmdTask>,
  projectWorkspaceIndex: ReadonlyMap<string, WorkspaceRef>,
): Map<string, string> {
  const candidatesByTitle = new Map<string, Set<string>>();

  for (const task of tasks) {
    const normalizedTitle = normalizeTaskTitle(task.title);
    if (normalizedTitle.length === 0) continue;
    const projectRef = task.projectId
      ? resolveRefFromIndex(projectWorkspaceIndex, task.projectId, "project")
      : null;
    const workspaceKey = resolveWorkspaceKeyFromRef({
      workspaceId: task.workspaceId ?? projectRef?.workspaceId ?? null,
      workspaceName: task.workspaceName ?? projectRef?.workspaceName ?? null,
    });
    if (!workspaceKey) continue;
    const keys = candidatesByTitle.get(normalizedTitle) ?? new Set<string>();
    keys.add(workspaceKey);
    candidatesByTitle.set(normalizedTitle, keys);
  }

  const uniqueByTitle = new Map<string, string>();
  for (const [title, keys] of candidatesByTitle.entries()) {
    if (keys.size !== 1) continue;
    const workspaceKey = keys.values().next().value;
    if (workspaceKey) {
      uniqueByTitle.set(title, workspaceKey);
    }
  }
  return uniqueByTitle;
}

function resolveRefFromIndex(
  index: ReadonlyMap<string, WorkspaceRef>,
  anchorId: string,
  kind: CmmdAnchorKind,
): WorkspaceRef | null {
  for (const key of buildAnchorLookupKeys(anchorId, kind)) {
    const ref = index.get(key);
    if (ref) return ref;
  }
  return null;
}

export function resolveThreadWorkspaceKey(input: {
  readonly thread: {
    readonly anchorKind: SidebarThreadSummary["anchorKind"];
    readonly anchorCmmdId: string | null;
    readonly projectId: string | null;
    readonly title: string;
  };
  readonly taskWorkspaceIndex: ReadonlyMap<string, WorkspaceRef>;
  readonly projectWorkspaceIndex: ReadonlyMap<string, WorkspaceRef>;
  readonly campaignWorkspaceIndex: ReadonlyMap<string, WorkspaceRef>;
  readonly workspaceKeyByTaskTitle: ReadonlyMap<string, string>;
}): string | null {
  const anchorId = input.thread.anchorCmmdId;
  if (anchorId) {
    if (input.thread.anchorKind === "task") {
      const ref = resolveRefFromIndex(input.taskWorkspaceIndex, anchorId, "task");
      const workspaceKey = resolveWorkspaceKeyFromRef(ref);
      if (workspaceKey) return workspaceKey;
    }
    if (input.thread.anchorKind === "project") {
      const ref = resolveRefFromIndex(input.projectWorkspaceIndex, anchorId, "project");
      const workspaceKey = resolveWorkspaceKeyFromRef(ref);
      if (workspaceKey) return workspaceKey;
    }
    if (input.thread.anchorKind === "campaign") {
      const ref = resolveRefFromIndex(input.campaignWorkspaceIndex, anchorId, "campaign");
      const workspaceKey = resolveWorkspaceKeyFromRef(ref);
      if (workspaceKey) return workspaceKey;
    }
  }

  if (input.thread.projectId) {
    const ref = resolveRefFromIndex(input.projectWorkspaceIndex, input.thread.projectId, "project");
    const workspaceKey = resolveWorkspaceKeyFromRef(ref);
    if (workspaceKey) return workspaceKey;
  }

  const normalizedTitle = normalizeTaskTitle(input.thread.title);
  if (normalizedTitle.length === 0) return null;
  return input.workspaceKeyByTaskTitle.get(normalizedTitle) ?? null;
}

export function resolveWorkspaceThreadCreationAnchor(input: {
  readonly workspaceId: string;
  readonly workspaceName: string | null;
  readonly unscopedWorkspaceId: string;
  readonly projects: ReadonlyArray<CmmdProject>;
  readonly referenceThread: WorkspaceScopedReferenceThread;
}): ThreadCreationAnchor | null {
  if (input.workspaceId === input.unscopedWorkspaceId) return null;

  const matchingProject = input.projects.find((project) =>
    projectMatchesWorkspace(project, input.workspaceId, input.workspaceName),
  );
  if (matchingProject) {
    return {
      anchorKind: "project",
      anchorCmmdId: matchingProject.id,
    };
  }

  if (input.referenceThread.anchorKind === "scratch") return null;
  if (!input.referenceThread.anchorCmmdId) return null;
  return {
    anchorKind: input.referenceThread.anchorKind,
    anchorCmmdId: input.referenceThread.anchorCmmdId,
  };
}
